import { FC, useEffect, useMemo, useState } from 'react'
import cn from './index.module.css'
import Modal from '@common/Modal'
import { useAddAlert } from '@/hooks/useAddAlert'
import Preloader from '@UI/Preloader'
import SecondaryButton from '@pages/Admin/CourseSettings/components/SecondaryButton/SecondaryButton'
import PromoCodeField from './components/PromoCodeField/PromoCodeField'
import {
  checkIsAllCoursesFree,
  getTariffOldPrice,
  getTariffPriceWithDiscount,
  getTotalPrice,
} from './utils/tariff'
import {
  courseEnrollment,
  IEnrollmentProfileData,
  fetchCourseAndTariffs,
  ICheckEmailOrPhoneResponse,
} from './utils/api'
import {
  CheckCircleOutlined,
  CloseOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { Spin } from 'antd'
import { useHistory } from 'react-router-dom'
import { checkIsEmail, checkIsPhone, checkPassword } from './utils/validation'
import { useGetSettings } from '../../tabs/CatalogCoursesTab/hooks/getPlatformSettings'
import { useSelector } from 'react-redux'
import { selectIsAuth, selectProfile } from '@store/profile.reducer'
import { crmApi, smsIntegrationApi } from '@/api'
import SelectAdditionalCourses from './components/SelectAdditionalCourses/SelectAdditionalCourses'
import SelectTariffValue, {
  getTariffValueMin,
} from './components/SelectTariffValue/SelectTariffValue'
import { ICourseEnrollmentItem } from './components/SelectAdditionalCourses/components/SelectCourseCard/SelectCourseCard'
import CoursePriceView from './components/CoursePriceView/CoursePriceView'
import ExtraPurchaseFieldsView from './components/ExtraPurchaseFieldsView/ExtraPurchaseFieldsView'
import { useGetExtraPurchaseFields } from '@pages/Admin/PlatformSettings2/DirectorySettings/components/ExtraPurchaseFields/utils'
import { LeadPaymentLink } from '@/api/types/crm.types'
import moment from 'moment'
import { usePlatformSettingsString } from '@/hooks/usePlatformSettingsString'

interface ICourseEnrollmentModal {
  visible: boolean
  crmLinkId?: number
  userId?: number
  courseId: number
  tariffId: number
  onDismiss: () => void
  onSuccess: () => void
}

const CourseEnrollmentModal: FC<ICourseEnrollmentModal> = ({
  visible,
  courseId,
  userId,
  tariffId,
  crmLinkId,
  onSuccess = () => {},
  onDismiss = () => {},
}) => {
  return (
    <Modal
      active={visible}
      setActive={onDismiss}
      // @ts-ignore
      className={cn.modalWrapper}
    >
      <ModalBody
        courseId={courseId}
        tariffId={tariffId}
        onSuccess={onSuccess}
        onDismiss={onDismiss}
        crmLinkId={crmLinkId}
        userId={userId}
      />
    </Modal>
  )
}

const ModalBody = ({
  courseId,
  tariffId,
  crmLinkId,
  userId,
  onSuccess = () => {},
  onDismiss = () => {},
}: {
  courseId: number
  tariffId: number
  crmLinkId?: number
  userId?: number
  onSuccess: () => void
  onDismiss: () => void
}) => {
  const isAuth = useSelector(selectIsAuth)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [course, setCourse] = useState<ICourse | null>(null)
  const [tariff, setTariff] = useState<ICourseTariff | null>(null)
  const [CRMLink, setCRMLink] = useState<LeadPaymentLink | null>(null)

  const fetchSettings = async () => {
    setIsLoading(true)
    try {
      if (courseId) {
        const res = await fetchCourseAndTariffs(courseId)
        if (res) {
          const tariff =
            res?.directionTariffs?.find((item) => item.id === tariffId) ?? null

          setCourse(res)
          setTariff(tariff)
        }
      }
      if (crmLinkId) {
        const classLink = (await crmApi.getLeadPaymentLink(crmLinkId)).data.data
        setCRMLink(classLink)
      }
    } catch (error) {
      console.error('Ошибка при загрузке данных:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchSettings()
  }, [])

  if (isLoading) {
    return (
      <div className={cn.modalContainer}>
        <div className={cn.preloader}>
          <Preloader />
        </div>
      </div>
    )
  }

  if (!tariff || !course) {
    return (
      <div className={cn.modalContainer}>
        <div className={cn.stub}>
          <div className={cn.modalContainer}>Такого курса или тарифа нет</div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn.modalContainer}>
      <div className={cn.modalHeader}>
        <span className={cn.modalHeaderTitle}>
          Регистрация на курс: {course?.name}
        </span>
        <CloseOutlined
          className={cn.closeIcon}
          onClick={onDismiss}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      </div>
      <div className={cn.modalSubHeader}>Тариф: {tariff?.name}</div>
      <div className={cn.contentWrapper}>
        <div className={cn.content}>
          {isAuth ? (
            <CourseEnrollment
              course={course}
              tariff={tariff}
              CRMLink={CRMLink}
              userId={userId}
              onSuccess={onSuccess}
            />
          ) : (
            <UnauthorizedCourseEnrollment
              course={course}
              tariff={tariff}
              CRMLink={CRMLink}
              userId={userId}
              onSuccess={onSuccess}
            />
          )}
          {tariff?.type !== 'free' && (
            <div className={cn.bottomLabel}>
              Курс добавляется не сразу. Нужно время, на обработку банком
              платежа
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const FormFooter = ({
  withExtraCourses,
  loading,
  disabledButton,
  tariff,
  promoCodeDiscount,
  price,
  oldPrice,
  withDemo,
  isFree,
  onAction,
}: {
  withExtraCourses: boolean
  loading: boolean
  disabledButton?: boolean
  tariff: ICourseTariff
  promoCodeDiscount: boolean
  price: number
  oldPrice: string
  withDemo: boolean
  isFree: boolean
  onAction: () => void
}) => {
  const isAuth = useSelector(selectIsAuth)
  const user = useSelector(selectProfile)
  const { checkSettings } = useGetSettings()
  const showOldPrice = checkSettings('catalog_showOldPrice')
  const freeEnrollText = usePlatformSettingsString(
    'isFreeEnrollButtonTitleChanged',
    'freeEnrollButtonTitle',
    'Записаться',
  )

  return (
    <div className={cn.footer}>
      <div className={cn.priceContainer}>
        {withExtraCourses ? (
          <div className={cn.promoContainer}>
            <div className={cn.currentPrice}>{price}р</div>
          </div>
        ) : (
          <>
            {tariff?.type === 'free' || withDemo ? (
              <div className={cn.currentPrice}>
                {withDemo ? 'Демо' : 'Бесплатно'}
              </div>
            ) : (
              <>
                <div className={cn.promoContainer}>
                  <div className={cn.currentPrice}>{price}р</div>
                  {promoCodeDiscount && (
                    <div className={cn.promoTitle}>с промокодом</div>
                  )}
                </div>
                {showOldPrice && oldPrice && (
                  <div className={cn.oldPrice}>{oldPrice}р</div>
                )}
              </>
            )}
          </>
        )}
      </div>
      {(!isAuth || user?.role === 'children') && (
        <div className={cn.footerActionWrapper}>
          {loading && (
            <Spin
              className={cn.footerActionSpinner}
              indicator={
                <LoadingOutlined
                  spin
                  onPointerEnterCapture={undefined}
                  onPointerLeaveCapture={undefined}
                />
              }
            />
          )}
          <SecondaryButton
            className={cn.actionButton}
            onClick={onAction}
            disabled={loading || disabledButton}
          >
            {isFree ? freeEnrollText : 'Купить'}
          </SecondaryButton>
        </div>
      )}
    </div>
  )
}

const FormFooterForHasCourse = ({}) => {
  const history = useHistory()

  const onClick = () => {
    history.push('/auth')
  }

  return (
    <>
      <div className={cn.hasCourseLabel}>
        На платформе существует кабинет с таким имейлом и в нём уже есть данный
        курс
      </div>
      <div className={cn.footer}>
        <div className={cn.footerActionWrapper}>
          <SecondaryButton className={cn.actionButton} onClick={onClick}>
            Войти
          </SecondaryButton>
        </div>
      </div>
    </>
  )
}

const SuccessEnrollmentStub = () => {
  const unpaidText = useSelector((store: any) => {
    return store.settings.isCustomPurchaseCardDescription === '1'
      ? store.settings.custom.customPurchaseCardDescription ?? null
      : null
  })

  return (
    <div className={cn.successStubWrapper}>
      <div className={cn.successStubWrapperIconWrapper}>
        <CheckCircleOutlined
          className={cn.successStubWrapperIcon}
          onPointerEnterCapture={undefined}
          onPointerLeaveCapture={undefined}
        />
      </div>
      <div className={cn.successStubWrapperColumn}>
        <div className={cn.successStubTitle}>Поздравляем с покупкой курса</div>
        <div className={cn.successStubDescription}>
          {unpaidText ??
            'Ожидайте в течение дня с вами свяжется персональный менеджер для уточнения информации'}
        </div>
      </div>
    </div>
  )
}

const CourseEnrollment = ({
  course,
  tariff,
  CRMLink,
  userId,
  onSuccess,
}: {
  course: ICourse
  tariff: ICourseTariff
  CRMLink?: LeadPaymentLink | null
  userId?: number
  onSuccess: () => void
}) => {
  const profile = useSelector(selectProfile)
  const addAlert = useAddAlert()
  const [loading, setLoading] = useState<boolean>(false)
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [selectedCourses, setSelectedCourses] = useState<
    ICourseEnrollmentItem[]
  >([])
  console.log('selectedCourses', selectedCourses)
  const { checkSettings, getSettings } = useGetSettings()
  const extraCoursesAllowed = checkSettings('isAllowedExtraCoursesPurchase')
  const discountsUnit = getSettings('extraCoursesDiscountUnit')
  const extraCoursesDiscounts = JSON.parse(
    getSettings('extraCoursesDiscounts') ?? 'null',
  )
  const [withDemo, setWithDemo] = useState<boolean>(false)

  const [promoCodeDiscount, setPromoCodeDiscount] =
    useState<IPromoCodeDiscount | null>(null)
  const [selectedValue, setSelectedValue] = useState<number>(
    getTariffValueMin(tariff),
  )

  useEffect(() => {
    if (CRMLink !== null && CRMLink?.value) {
      setSelectedValue(CRMLink?.value)
    }
  }, [CRMLink])

  const price = useMemo(() => {
    return getTariffPriceWithDiscount(tariff, selectedValue, promoCodeDiscount)
  }, [tariff.first_price, promoCodeDiscount, selectedValue])

  const oldPrice = useMemo(() => {
    return getTariffOldPrice(tariff, selectedValue)
  }, [tariff.old_price, selectedValue])

  const isAllFree = useMemo(() => {
    return checkIsAllCoursesFree([
      {
        course: course,
        tariffId: tariff.id,
        value: selectedValue,
        withDemo: withDemo,
        enrollDate: null,
      },
      ...selectedCourses,
    ])
  }, [course.id, tariff.id, selectedValue, withDemo, selectedCourses])

  const onEnroll = async () => {
    setLoading(true)

    if (course?.id && tariff.id) {
      let newWindow
      if (!isAllFree) {
        newWindow = window.open('', '_blank')
        if (newWindow) {
          newWindow.document.body.innerHTML = '<p>Подготовка к оплате...</p>'
        }
      }

      const res = await courseEnrollment(
        course?.id,
        tariff,
        withDemo,
        promoCodeDiscount ?? undefined,
        selectedValue,
        undefined,
        undefined,
        undefined,
        undefined,
        selectedCourses,
        extraCoursesDiscounts,
        discountsUnit ?? undefined,
        CRMLink?.id,
        userId,
      )
      if (res.status === 'success') {
        addAlert({
          title: 'Курс приобретен!',
        })
        setIsSuccess(true)
        const paymentLink = res?.data?.payment_link

        if (paymentLink && newWindow) {
          newWindow.location.href = paymentLink
        } else if (newWindow) {
          newWindow.document.body.innerText = 'Ссылка на оплату не найдена.'
        }
        onSuccess()
      } else {
        if (newWindow) {
          newWindow.document.body.innerText =
            'Произошла ошибка. Попробуйте позже.'
        }
        addAlert({
          title: 'Возникла ошибка!',
          type: 'error',
        })
      }
    }
    setLoading(false)
  }

  if (isSuccess) {
    return <SuccessEnrollmentStub />
  }

  const totalPrice = getTotalPrice(
    tariff,
    selectedValue,
    promoCodeDiscount,
    withDemo,
    selectedCourses,
    extraCoursesDiscounts,
    discountsUnit ?? undefined,
  )

  return (
    <div className={cn.container}>
      {tariff?.type !== 'free' && (
        <>
          <SelectTariffValue
            value={selectedValue}
            tariff={tariff}
            setValue={setSelectedValue}
            demo={withDemo}
            onChangeDemo={setWithDemo}
            canManage={!CRMLink?.value}
          />
          {!withDemo && CRMLink?.is_promo_enabled !== false && (
            <PromoCodeField
              userId={profile?.id}
              code={promoCodeDiscount}
              setCode={setPromoCodeDiscount}
            />
          )}
        </>
      )}
      {selectedCourses?.length > 0 && (
        <CoursePriceView
          tariff={tariff ?? undefined}
          value={selectedValue}
          promoCode={promoCodeDiscount ?? undefined}
          withDemo={withDemo}
        />
      )}
      {CRMLink?.enroll_date && (
        <div className={cn.enrollDateString}>
          Оплата за курс с {moment(CRMLink?.enroll_date).format('DD.MM.YYYY')}
        </div>
      )}
      {extraCoursesAllowed && (
        <SelectAdditionalCourses
          isFirstFree={withDemo ?? tariff.type === 'free'}
          currentCourseId={course?.id!!}
          addedCourses={selectedCourses}
          setAddedCourses={setSelectedCourses}
          canManage={!CRMLink}
        />
      )}
      <FormFooter
        withExtraCourses={selectedCourses?.length > 0}
        tariff={tariff}
        promoCodeDiscount={!!promoCodeDiscount}
        price={selectedCourses?.length > 0 ? totalPrice : price}
        oldPrice={oldPrice}
        loading={loading}
        withDemo={withDemo}
        isFree={isAllFree}
        onAction={onEnroll}
      />
    </div>
  )
}
const UnauthorizedCourseEnrollment = ({
  course,
  tariff,
  CRMLink,
  userId,
  onSuccess,
}: {
  course: ICourse
  tariff: ICourseTariff
  CRMLink?: LeadPaymentLink | null
  userId?: number
  onSuccess: () => void
}) => {
  const addAlert = useAddAlert()
  const history = useHistory()
  const [selectedCourses, setSelectedCourses] = useState<
    ICourseEnrollmentItem[]
  >([])
  const { checkSettings, getSettings, loadingSettings } = useGetSettings()
  const { extraFields } = useGetExtraPurchaseFields()

  const registrationTypeForCatalog =
    getSettings('registrationTypeForCatalog') ?? 'email' // тип регистрации
  const extraCoursesAllowed = checkSettings('isAllowedExtraCoursesPurchase')
  const discountsUnit = getSettings('extraCoursesDiscountUnit')
  const sms_integration_enabled = checkSettings('sms_integration_enabled') // активна ли интеграция с смс
  const confirmationByPhoneNumber = checkSettings('confirmationByPhoneNumber') // есть вариант подтвердить по смс на телефон
  const confirmationByTelegram = checkSettings('confirmationByTelegram') // есть вариант подтвердить по телеграмм
  const requiredNumberConfirmationForCatalog = checkSettings(
    'requiredNumberConfirmationForCatalog',
  ) // обязательно ли подтверждать телефон
  const extraCoursesDiscounts = JSON.parse(
    getSettings('extraCoursesDiscounts') ?? 'null',
  )
  const [userExistedData, setUserExistedData] =
    useState<ICheckEmailOrPhoneResponse>({
      // найденный пользователь с введенным email или телефоном
      user: null,
      hasCourse: false,
      roles: [],
      directions: [],
    })
  const [parentExistedData, setParentExistedData] =
    useState<ICheckEmailOrPhoneResponse>({
      user: null,
      hasCourse: false,
      roles: [],
      directions: [],
    })
  const [code, setCode] = useState('') //код для подтверждения
  const [token, setToken] = useState('') //токен для подтверждения
  const [loading, setLoading] = useState<boolean>(false)
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [codeDeliveryMethod, setCodeDeliveryMethod] = useState('') // 'sms' или 'telegram'//выбранный метод пользователем
  const [validationErrors, setValidationErrors] = useState<
    (keyof IEnrollmentProfileData)[]
  >([])
  const [user, setUser] = useState<IEnrollmentProfileData>({
    studentName: '',
    studentEmail: '',
    studentPhone: '',
    password: '',
    parentName: '',
    parentPhone: '',
    parentEmail: '',
    parentPassword: '',
    customText: '',
  })

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)

    const updatedUser = { ...user }

    for (const key of params.keys()) {
      if (key in updatedUser) {
        const value = params.get(key)
        if (value !== null) {
          ;(updatedUser as any)[key] = value.slice(0, 200)
        }
      }
    }

    setUser(updatedUser)
  }, [])

  const [promoCodeDiscount, setPromoCodeDiscount] =
    useState<IPromoCodeDiscount | null>(null)
  const [withDemo, setWithDemo] = useState<boolean>(false)
  const [selectedValue, setSelectedValue] = useState<number>(
    CRMLink?.value ?? getTariffValueMin(tariff),
  )
  const showAdditionalField = !userExistedData?.user
  const showParentAdditionalField = !parentExistedData?.user

  useEffect(() => {
    if (CRMLink !== null && CRMLink?.value) {
      setSelectedValue(CRMLink?.value)
    }
  }, [CRMLink])

  const price = useMemo(() => {
    return getTariffPriceWithDiscount(tariff, selectedValue, promoCodeDiscount)
  }, [tariff.first_price, promoCodeDiscount, selectedValue])

  const oldPrice = useMemo(() => {
    return getTariffOldPrice(tariff, selectedValue)
  }, [tariff.old_price, selectedValue])

  const isAllFree = useMemo(() => {
    return checkIsAllCoursesFree([
      {
        course: course,
        tariffId: tariff.id,
        value: selectedValue,
        withDemo: withDemo,
        enrollDate: null,
      },
      ...selectedCourses,
    ])
  }, [course.id, tariff.id, selectedValue, withDemo, selectedCourses])

  const validateForm = (): boolean => {
    const errors: string[] = []
    const userWrongRole = !userExistedData?.roles?.includes('children')
    if (!!userExistedData.user && userWrongRole) {
      addAlert({
        title:
          'Данная почта привязана к личному кабинету сотрудника/родителя, впишите другую',
        type: 'error',
      })
      return false
    }
    if (
      !userId && // если передан user_id из crm, то не проверяем поля студента
      showAdditionalField &&
      extraFields.includes('studentName') &&
      user?.studentName.length < 3
    ) {
      errors.push('studentName')
    }
    if (
      !userId && // если передан user_id из crm, то не проверяем поля студента
      !checkIsEmail(user.studentEmail) &&
      extraFields.includes('studentEmail')
    ) {
      errors.push('studentEmail')
    }
    if (
      !userId && // если передан user_id из crm, то не проверяем поля студента
      !checkIsPhone(user.studentPhone) &&
      extraFields.includes('studentPhone')
    ) {
      errors.push('studentPhone')
    }
    if (
      !userId && // если передан user_id из crm, то не проверяем поля студента
      showAdditionalField &&
      extraFields.includes('password') &&
      !checkPassword(user.password)
    ) {
      if (!checkPassword(user.parentPassword)) {
        addAlert({ text: 'Пароль не должен содержать кирилицу', type: 'error' })
      }
      errors.push('password')
    }
    if (
      showAdditionalField &&
      showParentAdditionalField &&
      extraFields.includes('parentName') &&
      user.parentName.length < 3
    ) {
      errors.push('parentName')
    }
    if (
      showAdditionalField &&
      extraFields.includes('parentEmail') &&
      (!checkIsEmail(user.parentEmail) ||
        user.parentEmail === user.studentEmail)
    ) {
      errors.push('parentEmail')
    }
    if (
      showAdditionalField &&
      extraFields.includes('parentPhone') &&
      (!checkIsPhone(user.parentPhone) ||
        user.parentPhone === user.studentPhone)
    ) {
      errors.push('parentPhone')
    }
    if (
      showAdditionalField &&
      showParentAdditionalField &&
      extraFields.includes('parentPassword') &&
      !checkPassword(user.parentPassword)
    ) {
      if (!checkPassword(user.parentPassword)) {
        addAlert({ text: 'Пароль не должен содержать кирилицу', type: 'error' })
      }
      errors.push('parentPassword')
    }

    setValidationErrors(
      (prev) => Array.from(new Set(errors)) as (keyof IEnrollmentProfileData)[],
    )

    if (errors?.[0]) {
      let error = 'Ошибка на форме'
      switch (errors?.[0] as keyof IEnrollmentProfileData) {
        case 'studentName':
          error = 'Заполните ФИО'
          break
        case 'studentEmail':
          error = 'Проверьте Email'
          break
        case 'studentPhone':
          error = 'Проверьте Телефон'
          break
        case 'password':
          error = 'Пароль должен быть заполнен'
          break
        case 'parentName':
          error = 'Заполните ФИО родителя'
          break
        case 'parentEmail': {
          if (user.parentEmail === user.studentEmail) {
            error = 'Email родителя и учащегося не должны совпадать'
          } else {
            error = 'Проверьте Email родителя'
          }
          break
        }
        case 'parentPhone':
          if (user.parentPhone === user.studentPhone) {
            error = 'Телефон родителя и учащегося не должны совпадать'
          } else {
            error = 'Проверьте поле Телефон родителя'
          }
          break

        default:
          break
      }

      addAlert({
        title: error,
        type: 'error',
      })
      return false
    }
    return true
  }

  const onEnroll = async () => {
    setLoading(true)

    if (course?.id && tariff.id && validateForm()) {
      let newWindow
      if (!isAllFree) {
        newWindow = window.open('', '_blank')
        if (newWindow) {
          newWindow.document.body.innerHTML = '<p>Подготовка к оплате...</p>'
        }
      }
      const res = await courseEnrollment(
        course?.id,
        tariff,
        withDemo,
        promoCodeDiscount ?? undefined,
        selectedValue,
        user,
        token,
        code,
        !!userExistedData?.user,
        selectedCourses,
        extraCoursesDiscounts,
        discountsUnit ?? undefined,
        CRMLink?.id,
        userId,
      )
      if (res.status === 'success') {
        addAlert({
          title: 'Курс приобретен!',
        })
        const paymentLink = res?.data?.payment_link

        if (paymentLink && newWindow) {
          newWindow.location.href = paymentLink
        } else if (newWindow) {
          newWindow.document.body.innerText = 'Ссылка на оплату не найдена.'
        }
        setIsSuccess(true)
        onSuccess()
        history.push('/auth')
      } else {
        if (newWindow) {
          newWindow.document.body.innerText =
            'Произошла ошибка. Попробуйте позже.'
        }
        addAlert({
          title: 'Возникла ошибка!',
          type: 'error',
        })
      }
    }
    setLoading(false)
  }

  if (loadingSettings) {
    return <Preloader />
  }

  if (isSuccess) {
    return <SuccessEnrollmentStub />
  }

  const totalPrice = getTotalPrice(
    tariff,
    selectedValue,
    promoCodeDiscount,
    withDemo,
    selectedCourses,
    extraCoursesDiscounts,
    discountsUnit ?? undefined,
  )

  const updateUser = (field: keyof IEnrollmentProfileData, value: string) => {
    setUser((prev) => {
      return { ...prev, [field]: value }
    })
  }

  const userHasCourse = userExistedData?.user && userExistedData?.hasCourse

  const getTokenFromServer = () => {
    smsIntegrationApi
      .getToken({
        phone: user.studentPhone,
        type: [codeDeliveryMethod],
      })
      .then((res) => {
        if (res?.data?.status === 'success') {
          setToken(res?.data?.token)
          addAlert({
            title: 'Код отправлен',
            text: 'Заполните поле с кодом подтверждения',
          })
        } else {
          addAlert({
            title: 'Ошибка',
            // @ts-ignore
            text: res?.response?.data?.error,
            type: 'error',
          })
        }
      })
  }

  const smsIntegrationEnabled =
    sms_integration_enabled &&
    requiredNumberConfirmationForCatalog &&
    (confirmationByPhoneNumber || confirmationByTelegram) &&
    registrationTypeForCatalog === 'phone' &&
    !userExistedData.user
  // нужно ли подтверждать номер исходя их всех условий

  const getToken = !token && !userId && smsIntegrationEnabled // нужно ли получать токен

  return (
    <div className={cn.container}>
      <ExtraPurchaseFieldsView
        courseId={course.id!}
        userId={userId}
        profile={user}
        updateProfile={updateUser}
        token={token}
        code={code}
        setCode={setCode}
        userExistedData={userExistedData}
        setUserExistedData={setUserExistedData}
        parentExistedData={parentExistedData}
        setParentExistedData={setParentExistedData}
        codeDeliveryMethod={codeDeliveryMethod}
        setCodeDeliveryMethod={setCodeDeliveryMethod}
        validationErrors={validationErrors}
        setValidationErrors={setValidationErrors}
      />
      {!userHasCourse && tariff?.type !== 'free' && (
        <>
          <SelectTariffValue
            value={selectedValue}
            tariff={tariff}
            setValue={setSelectedValue}
            demo={withDemo}
            onChangeDemo={setWithDemo}
            canManage={!CRMLink?.value}
          />
          {!withDemo && CRMLink?.is_promo_enabled !== false && (
            <PromoCodeField
              userId={userExistedData?.user?.id ?? null}
              code={promoCodeDiscount}
              setCode={setPromoCodeDiscount}
            />
          )}
        </>
      )}
      {selectedCourses?.length > 0 && (
        <CoursePriceView
          tariff={tariff ?? undefined}
          value={selectedValue}
          promoCode={promoCodeDiscount ?? undefined}
          withDemo={withDemo}
        />
      )}
      {CRMLink?.enroll_date && (
        <div className={cn.enrollDateString}>
          Оплата за курс с {moment(CRMLink?.enroll_date).format('DD.MM.YYYY')}
        </div>
      )}
      {extraCoursesAllowed && (
        <SelectAdditionalCourses
          userCourses={userExistedData.directions}
          isFirstFree={withDemo || tariff.type === 'free'}
          currentCourseId={course?.id!!}
          addedCourses={selectedCourses}
          setAddedCourses={setSelectedCourses}
          canManage={!CRMLink}
        />
      )}
      {userHasCourse ? (
        <FormFooterForHasCourse />
      ) : (
        <FormFooter
          withExtraCourses={selectedCourses?.length > 0}
          tariff={tariff}
          promoCodeDiscount={!!promoCodeDiscount}
          price={selectedCourses?.length > 0 ? totalPrice : price}
          oldPrice={oldPrice}
          withDemo={withDemo}
          loading={loading}
          isFree={isAllFree}
          onAction={getToken ? getTokenFromServer : onEnroll}
          disabledButton={
            !!parentExistedData.user &&
            !parentExistedData?.roles?.includes('parent')
          }
        />
      )}
    </div>
  )
}

export default CourseEnrollmentModal
